// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {BaseTabs} from "@/components";
import closeIcon from "../../assets/images/floating-menu-icons/cancelX.svg";
import BetbyWidgetContainer from "../BetbyWidgetContainer";
import GameGridModal from "./GameGridModal";

const modalOverlayCls =
  "fixed inset-0 bg-[rgba(0,0,0,0.7)] backdrop-blur-[4px] z-[1500] flex items-center justify-center p-5 animate-modal-fade-in max-[480px]:p-2.5";
const modalContainerCls =
  "relative bg-[linear-gradient(180deg,var(--color-surface-2)_0%,var(--color-surface)_100%)] rounded-[var(--radius-xl)] w-full max-w-[620px] max-h-[86vh] overflow-hidden shadow-[0_25px_50px_rgba(0,0,0,0.5)] border border-[var(--color-border)] animate-modal-slide-up before:content-[''] before:absolute before:inset-0 before:bg-[linear-gradient(180deg,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0.01)_100%)] before:pointer-events-none before:rounded-[var(--radius-xl)] [&_.ant-tabs]:text-[var(--color-text)] [&_.ant-tabs-tab]:text-[var(--color-text-secondary)] [&_.ant-tabs-tab]:font-semibold [&_.ant-tabs-tab]:rounded-none [&_.ant-tabs-tab]:mx-2 [&_.ant-tabs-tab]:bg-transparent hover:[&_.ant-tabs-tab]:text-[var(--color-text)] [&_.ant-tabs-tab-active]:!text-[var(--color-primary)] [&_.ant-tabs-tab-active_*]:!text-[var(--color-primary)] [&_.ant-tabs-ink-bar]:bg-[var(--color-primary)] [&_.ant-tabs-ink-bar]:h-[3px] [&_.ant-tabs-ink-bar]:rounded-[var(--radius-sm)] [&_.ant-tabs-content-holder]:max-h-[56vh] [&_.ant-tabs-content-holder]:overflow-y-auto [&_.ant-tabs-nav]:mb-4 [&_.ant-tabs-nav]:before:border-b [&_.ant-tabs-nav]:before:border-[var(--color-border)] [&_.ant-tabs-content-holder::-webkit-scrollbar]:w-[6px] [&_.ant-tabs-content-holder::-webkit-scrollbar-track]:bg-[var(--scrollbar-track,transparent)] [&_.ant-tabs-content-holder::-webkit-scrollbar-track]:rounded-[var(--radius-sm)] [&_.ant-tabs-content-holder::-webkit-scrollbar-thumb]:bg-[var(--scrollbar-thumb,rgba(255,255,255,0.18))] [&_.ant-tabs-content-holder::-webkit-scrollbar-thumb]:rounded-[var(--radius-sm)] hover:[&_.ant-tabs-content-holder::-webkit-scrollbar-thumb]:bg-[var(--scrollbar-thumb-hover,rgba(255,255,255,0.28))] [&_.ant-alert]:max-w-full [&_.ant-alert]:box-border [&_.ant-alert]:overflow-hidden [&_.ant-alert]:break-words [&_.ant-alert-message]:break-words [&_.ant-alert-description]:break-words max-[480px]:!max-w-full max-[480px]:!max-h-[92vh] max-[480px]:!rounded-[var(--radius-xl)]";
const modalHeaderCls =
  "px-6 pt-6 pb-4 flex items-center justify-between border-b border-[var(--color-alpha-surface-faint)] relative z-[var(--z-base)] max-[480px]:px-5 max-[480px]:pt-5 max-[480px]:pb-[var(--space-4)]";
const modalTitleCls = "text-xl font-bold text-[var(--color-text)] m-0 flex items-center gap-2 max-[480px]:text-lg";
const closeButtonCls =
  "bg-[var(--color-surface-pressed)] border border-[var(--color-border)] rounded-full w-9 h-9 flex items-center justify-center cursor-pointer transition-all duration-300 hover:border-[var(--color-lottery)] hover:bg-[var(--color-lottery)]/10 hover:scale-110";
const closeIconCls = "w-4 h-4 brightness-0 invert";
const modalContentCls = "px-6 py-5 relative z-[var(--z-base)] max-[480px]:px-5 max-[480px]:py-4";
const modalTabsCls =
  "w-full [&_.ant-tabs-content-holder]:max-h-[450px] [&_.ant-tabs-content-holder]:overflow-auto max-[480px]:[&_.ant-tabs-content-holder]:max-h-[var(--size-100)]";
const modalDescriptionCls =
  "text-[var(--color-text-secondary)] text-sm mx-0 my-0 mb-5 text-center leading-[1.5] max-[480px]:text-[var(--font-body)] max-[480px]:mb-4";
const modalFooterCls =
  "px-6 pt-4 pb-6 flex gap-3 justify-center relative z-[var(--z-base)] max-[480px]:px-5 max-[480px]:pt-[var(--space-4)] max-[480px]:pb-5 max-[480px]:flex-col";
const hideButtonCls =
  "py-2.5 px-5 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 border border-[var(--color-lottery)] bg-transparent text-[var(--color-lottery)] hover:bg-[var(--color-lottery)]/10 hover:-translate-y-px max-[480px]:py-3 max-[480px]:px-4 max-[480px]:text-[var(--font-body)]";
const minimizeButtonCls =
  "py-2.5 px-5 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 border border-[var(--color-lottery)] bg-[var(--color-lottery)] text-white hover:opacity-90 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(255,3,96,0.28)] max-[480px]:py-3 max-[480px]:px-4 max-[480px]:text-[var(--font-body)]";

const MultiGameModal = ({isOpen, onClose, onMinimize, games, onGameSelect}) => {
  const {t} = useTranslation();
  const modalRef = useRef(null);
  const [activeTab, setActiveTab] = useState("casino");

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEscKey = event => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className={modalOverlayCls}>
      <div
        className={modalContainerCls}
        ref={modalRef}
      >
        <div className={modalHeaderCls}>
          <h3 className={modalTitleCls}>ðŸŽ® {t("quickGamesTitle", "Quick Games")}</h3>
          <button
            type="button"
            className={closeButtonCls}
            onClick={onClose}
            aria-label="Close modal"
          >
            <img
              src={closeIcon}
              alt="Close"
              className={closeIconCls}
            />
          </button>
        </div>

        <div className={modalContentCls}>
          <p className={modalDescriptionCls}>
            {t("quickGamesDescription", "Keep playing while waiting for your main game!")}
          </p>

          <BaseTabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "casino",
                label: <span style={{fontSize: "var(--font-h3)", fontWeight: "600"}}>{t("casino", "Casino")}</span>,
                children: (
                  <div style={{padding: "16px 0"}}>
                    <GameGridModal
                      games={games}
                      onGameSelect={onGameSelect}
                    />
                  </div>
                ),
              },
              {
                key: "sports",
                label: <span style={{fontSize: "var(--font-h3)", fontWeight: "600"}}>{t("sports", "Sports")}</span>,
                children: (
                  <div style={{padding: "8px 0", height: "var(--size-125)", overflow: "auto"}}>
                    <BetbyWidgetContainer />
                  </div>
                ),
              },
            ]}
            tabBarClassName={modalTabsCls}
          />
        </div>

        <div className={modalFooterCls}>
          <button
            type="button"
            className={hideButtonCls}
            onClick={onClose}
          >
            {t("hideQuickGames", "Hide")}
          </button>
          <button
            type="button"
            className={minimizeButtonCls}
            onClick={onMinimize}
          >
            {t("minimizeQuickGames", "Minimize")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

MultiGameModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func.isRequired,
  games: PropTypes.arrayOf(PropTypes.object).isRequired,
  onGameSelect: PropTypes.func.isRequired,
};

export default MultiGameModal;
